import React, { useEffect, useState } from 'react'
import { Alert, Badge, Card, Col, Container, Row, Spinner, Table, Button } from 'react-bootstrap'
import { Link, useNavigate } from 'react-router-dom'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { TRAINING_TYPES, fmtDateTH, fmtDateTimeTH, fmtDays, fmtPeriodRange } from './hrShared'

// การ์ดใบลารออนุมัติ 1 รายการ สำหรับมุมมองมือถือ
const PendingLeaveCard = ({ r, onClick }) => (
  <div className="border rounded p-3" style={{ cursor: 'pointer' }} onClick={onClick}>
    <div className="d-flex align-items-start justify-content-between gap-2">
      <div className="fw-semibold">{r.t_name}</div>
      <Badge bg="info">{r.lt_name}</Badge>
    </div>
    <div className="mt-2 pt-2 border-top fs-sm">
      <div><span className="text-muted">วันที่ลา:</span> {fmtDateTH(r.lr_start_date)} - {fmtDateTH(r.lr_end_date)} ({fmtDays(r.lr_days)} วัน)</div>
      {r.lr_reason && <div><span className="text-muted">เหตุผล:</span> {r.lr_reason}</div>}
      <div className="text-muted fs-xs mt-1">ยื่นเมื่อ {fmtDateTimeTH(r.created_at)}</div>
    </div>
  </div>
)

// การ์ดคำขอออกนอกสถานที่รออนุมัติ 1 รายการ สำหรับมุมมองมือถือ
const PendingOffsiteCard = ({ r, onClick }) => (
  <div className="border rounded p-3" style={{ cursor: 'pointer' }} onClick={onClick}>
    <div className="fw-semibold">{r.t_name}</div>
    <div className="mt-2 pt-2 border-top fs-sm">
      <div><span className="text-muted">วันที่:</span> {fmtDateTH(r.os_date)}</div>
      <div><span className="text-muted">เวลาออก - กลับ:</span> {r.os_time_out} - {r.os_time_return}</div>
      {r.os_location && <div><span className="text-muted">สถานที่:</span> {r.os_location}</div>}
      {r.os_purpose && <div><span className="text-muted">วัตถุประสงค์:</span> {r.os_purpose}</div>}
      <div className="text-muted fs-xs mt-1">ยื่นเมื่อ {fmtDateTimeTH(r.created_at)}</div>
    </div>
  </div>
)

// การ์ดรายการแจ้งไปอบรม/ประชุมรออนุมัติ 1 รายการ สำหรับมุมมองมือถือ
const PendingTrainingCard = ({ r, onClick }) => {
  const tt = TRAINING_TYPES[r.tr_type] || TRAINING_TYPES.training
  return (
    <div className="border rounded p-3" style={{ cursor: 'pointer' }} onClick={onClick}>
      <div className="d-flex align-items-start justify-content-between gap-2">
        <div className="fw-semibold">{r.t_name}</div>
        <Badge bg={tt.bg}>{tt.label}</Badge>
      </div>
      <div className="mt-2 pt-2 border-top fs-sm">
        <div><span className="text-muted">หัวข้อ:</span> {r.tr_title}</div>
        <div><span className="text-muted">วันที่:</span> {fmtDateTH(r.tr_start_date)} - {fmtDateTH(r.tr_end_date)} ({r.tr_days} วัน)</div>
        <div><span className="text-muted">ช่วงเวลา:</span> {fmtPeriodRange(r.tr_period_start, r.tr_period_end) || 'ทั้งวัน'}</div>
        {r.tr_location && <div><span className="text-muted">สถานที่:</span> {r.tr_location}</div>}
        <div className="text-muted fs-xs mt-1">ยื่นเมื่อ {fmtDateTimeTH(r.created_at)}</div>
      </div>
    </div>
  )
}

export default function HrDashboard() {
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [leaveView, setLeaveView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table')
  const [offsiteView, setOffsiteView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table')
  const [trainingView, setTrainingView] = useState(() =>
    (typeof window !== 'undefined' && window.innerWidth < 768) ? 'card' : 'table')

  useEffect(() => {
    axiosInstance.get('/api/hr/dashboard')
      .then(res => { if (res.data.success) setData(res.data.data) })
      .catch(err => setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ'))
      .finally(() => setLoading(false))
  }, [])

  const cards = [
    { label: 'บุคลากรทั้งหมด', value: data?.totalStaff, icon: 'ri-team-line', color: 'primary', to: '/hr/staff' },
    { label: 'ปฏิบัติงาน', value: data?.activeStaff, icon: 'ri-user-follow-line', color: 'success', to: '/hr/staff' },
    { label: 'ลาวันนี้', value: data?.onLeaveToday, icon: 'ri-calendar-event-line', color: 'warning', to: '/hr/leave-approval' },
    { label: 'ออกนอกสถานที่วันนี้', value: data?.offsiteToday, icon: 'ri-walk-line', color: 'info', to: '/hr/offsite-approval' },
    { label: 'ไปอบรม/ประชุม/กิจกรรมวันนี้', value: data?.trainingToday, icon: 'ri-presentation-line', color: 'secondary', to: '/hr/training-approval' },
  ]

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between mb-4">
            <h4 className="main-title mb-0">
              <i className="ri-dashboard-2-line me-2 text-primary" />
              ภาพรวมงานบุคลากร
            </h4>
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : (
            <>
              <Row className="g-3 mb-4">
                {cards.map((c, i) => (
                  <Col xs={6} lg={3} key={i}>
                    <Card
                      as={c.to ? Link : 'div'}
                      to={c.to}
                      className="card-one text-decoration-none"
                      style={c.to ? { cursor: 'pointer' } : undefined}
                    >
                      <Card.Body className="d-flex align-items-center gap-3">
                        <div className={`avatar bg-${c.color}-subtle text-${c.color} rounded d-flex align-items-center justify-content-center`} style={{ width: 48, height: 48 }}>
                          <i className={`${c.icon} fs-3`} />
                        </div>
                        <div>
                          <div className="text-muted fs-sm">{c.label}</div>
                          <h3 className="mb-0">{c.value ?? '-'}</h3>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>

              <Card className="card-one">
                <Card.Header className="d-flex align-items-center justify-content-between gap-2">
                  <Card.Title as="h6" className="mb-0">ใบลา รออนุมัติล่าสุด</Card.Title>
                  <div className="d-flex align-items-center gap-2">
                    <div className="btn-group btn-group-sm" role="group" aria-label="สลับมุมมอง">
                      <button type="button" className={`btn btn${leaveView === 'table' ? '' : '-outline'}-primary`}
                        onClick={() => setLeaveView('table')} title="มุมมองตาราง">
                        <i className="ri-table-line" />
                      </button>
                      <button type="button" className={`btn btn${leaveView === 'card' ? '' : '-outline'}-primary`}
                        onClick={() => setLeaveView('card')} title="มุมมองการ์ด">
                        <i className="ri-layout-grid-line" />
                      </button>
                    </div>
                    <Button as={Link} to="/hr/leave-approval" variant="outline-primary" size="sm">
                      ดูทั้งหมด <i className="ri-arrow-right-line" />
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  {(data?.pendingList || []).length === 0 ? (
                    <div className="text-center text-muted py-4">ไม่มีใบลารออนุมัติ</div>
                  ) : leaveView === 'card' ? (
                    <div className="p-3">
                      <Row className="g-3">
                        {data.pendingList.map(r => (
                          <Col key={r.lr_id} xs={12} md={6} xl={4}>
                            <PendingLeaveCard r={r} onClick={() => navigate('/hr/leave-approval')} />
                          </Col>
                        ))}
                      </Row>
                    </div>
                  ) : (
                  <Table responsive hover className="mb-0 fs-sm">
                    <thead className="table-light">
                      <tr>
                        <th>ชื่อ-สกุล</th>
                        <th>ประเภทการลา</th>
                        <th>วันที่ลา</th>
                        <th className="text-end">จำนวนวัน</th>
                        <th>เหตุผล</th>
                        <th>ยื่นเมื่อ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.pendingList.map(r => (
                        <tr key={r.lr_id} style={{ cursor: 'pointer' }} onClick={() => navigate('/hr/leave-approval')}>
                          <td>{r.t_name}</td>
                          <td><Badge bg="info">{r.lt_name}</Badge></td>
                          <td>{fmtDateTH(r.lr_start_date)} - {fmtDateTH(r.lr_end_date)}</td>
                          <td className="text-end">{fmtDays(r.lr_days)}</td>
                          <td className="text-truncate" style={{ maxWidth: 200 }}>{r.lr_reason || '-'}</td>
                          <td>{fmtDateTimeTH(r.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  )}
                </Card.Body>
              </Card>

              <Card className="card-one mt-4">
                <Card.Header className="d-flex align-items-center justify-content-between gap-2">
                  <Card.Title as="h6" className="mb-0">คำขอออกนอกสถานที่ รออนุมัติล่าสุด</Card.Title>
                  <div className="d-flex align-items-center gap-2">
                    <div className="btn-group btn-group-sm" role="group" aria-label="สลับมุมมอง">
                      <button type="button" className={`btn btn${offsiteView === 'table' ? '' : '-outline'}-primary`}
                        onClick={() => setOffsiteView('table')} title="มุมมองตาราง">
                        <i className="ri-table-line" />
                      </button>
                      <button type="button" className={`btn btn${offsiteView === 'card' ? '' : '-outline'}-primary`}
                        onClick={() => setOffsiteView('card')} title="มุมมองการ์ด">
                        <i className="ri-layout-grid-line" />
                      </button>
                    </div>
                    <Button as={Link} to="/hr/offsite-approval" variant="outline-info" size="sm">
                      ดูทั้งหมด <i className="ri-arrow-right-line" />
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  {(data?.pendingOffsiteList || []).length === 0 ? (
                    <div className="text-center text-muted py-4">ไม่มีคำขอออกนอกสถานที่รออนุมัติ</div>
                  ) : offsiteView === 'card' ? (
                    <div className="p-3">
                      <Row className="g-3">
                        {data.pendingOffsiteList.map(r => (
                          <Col key={r.os_id} xs={12} md={6} xl={4}>
                            <PendingOffsiteCard r={r} onClick={() => navigate('/hr/offsite-approval')} />
                          </Col>
                        ))}
                      </Row>
                    </div>
                  ) : (
                  <Table responsive hover className="mb-0 fs-sm">
                    <thead className="table-light">
                      <tr>
                        <th>ชื่อ-สกุล</th>
                        <th>วันที่</th>
                        <th>เวลาออก - กลับ</th>
                        <th>สถานที่</th>
                        <th>วัตถุประสงค์</th>
                        <th>ยื่นเมื่อ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.pendingOffsiteList.map(r => (
                        <tr key={r.os_id} style={{ cursor: 'pointer' }} onClick={() => navigate('/hr/offsite-approval')}>
                          <td>{r.t_name}</td>
                          <td>{fmtDateTH(r.os_date)}</td>
                          <td>{r.os_time_out} - {r.os_time_return}</td>
                          <td className="text-truncate" style={{ maxWidth: 160 }}>{r.os_location || '-'}</td>
                          <td className="text-truncate" style={{ maxWidth: 200 }}>{r.os_purpose || '-'}</td>
                          <td>{fmtDateTimeTH(r.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  )}
                </Card.Body>
              </Card>

              <Card className="card-one mt-4">
                <Card.Header className="d-flex align-items-center justify-content-between gap-2">
                  <Card.Title as="h6" className="mb-0">แจ้งไปอบรม/ประชุม/กิจกรรม รออนุมัติล่าสุด</Card.Title>
                  <div className="d-flex align-items-center gap-2">
                    <div className="btn-group btn-group-sm" role="group" aria-label="สลับมุมมอง">
                      <button type="button" className={`btn btn${trainingView === 'table' ? '' : '-outline'}-primary`}
                        onClick={() => setTrainingView('table')} title="มุมมองตาราง">
                        <i className="ri-table-line" />
                      </button>
                      <button type="button" className={`btn btn${trainingView === 'card' ? '' : '-outline'}-primary`}
                        onClick={() => setTrainingView('card')} title="มุมมองการ์ด">
                        <i className="ri-layout-grid-line" />
                      </button>
                    </div>
                    <Button as={Link} to="/hr/training-approval" variant="outline-secondary" size="sm">
                      ดูทั้งหมด <i className="ri-arrow-right-line" />
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  {(data?.pendingTrainingList || []).length === 0 ? (
                    <div className="text-center text-muted py-4">ไม่มีรายการแจ้งไปอบรม/ประชุม/กิจกรรมรออนุมัติ</div>
                  ) : trainingView === 'card' ? (
                    <div className="p-3">
                      <Row className="g-3">
                        {data.pendingTrainingList.map(r => (
                          <Col key={r.tr_id} xs={12} md={6} xl={4}>
                            <PendingTrainingCard r={r} onClick={() => navigate('/hr/training-approval')} />
                          </Col>
                        ))}
                      </Row>
                    </div>
                  ) : (
                  <Table responsive hover className="mb-0 fs-sm">
                    <thead className="table-light">
                      <tr>
                        <th>ชื่อ-สกุล</th>
                        <th>ประเภท</th>
                        <th>หัวข้อ</th>
                        <th>วันที่</th>
                        <th className="text-end">จำนวนวัน</th>
                        <th>ยื่นเมื่อ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.pendingTrainingList.map(r => {
                        const tt = TRAINING_TYPES[r.tr_type] || TRAINING_TYPES.training
                        return (
                          <tr key={r.tr_id} style={{ cursor: 'pointer' }} onClick={() => navigate('/hr/training-approval')}>
                            <td>{r.t_name}</td>
                            <td><Badge bg={tt.bg}>{tt.label}</Badge></td>
                            <td className="text-truncate" style={{ maxWidth: 220 }}>{r.tr_title}</td>
                            <td>{fmtDateTH(r.tr_start_date)} - {fmtDateTH(r.tr_end_date)}</td>
                            <td className="text-end">{r.tr_days}</td>
                            <td>{fmtDateTimeTH(r.created_at)}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </Table>
                  )}
                </Card.Body>
              </Card>
            </>
          )}
        </Container>
      </div>
      <Footer />
    </>
  )
}
